import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CarouselProvider,
  Dot,
  Image,
  ImageWithZoom,
  Slide,
  Slider
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Product } from '../../../models/Product/Product';
import { Media } from '../../../models/Media/Media';
import collect, { Collection } from 'collect.js';
import {
  ProductAttribute,
  ProductType
} from '../../../models/Product/contracts';
import style from './single-product.module.scss';
import AttributeGroup from '../../../Shop/components/Attributes/AttributeGroup';
import { MdQrCode } from 'react-icons/md';
import QRCode from 'qrcode.react';

interface ProductDetailsProps {
  product: Product;
  setSelectedProduct: (product: Product) => void;
}

function VariableProduct({ product, setSelectedProduct }: ProductDetailsProps) {
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<{
    [key: string]: number;
  }>({});

  const attributes = useMemo(
    () =>
      collect(product.attributes ?? [])
        .groupBy<Collection<ProductAttribute>, string>('name')
        .map<ProductAttribute[]>((attributes) => attributes.toArray()),
    [product.attributes]
  );

  const variations = useMemo(() => {
    const variationMap = new Map<string, Product>();
    product?.variations?.forEach((variation) => {
      const key =
        variation.attributes
          ?.map(({ id }) => id)
          .sort((a, b) => a - b)
          .join('-') ?? '';
      if (key) {
        variationMap.set(key, variation);
      }
    });
    return variationMap;
  }, [product?.variations]);

  useEffect(() => {
    if (Object.keys(selectedAttributeValues).length === 0) return;

    const key = Object.values(selectedAttributeValues)
      .sort((a, b) => a - b)
      .join('-');
    const selectedVariation = variations.get(key);

    if (selectedVariation) {
      setSelectedProduct(selectedVariation);
    }
  }, [selectedAttributeValues, variations, setSelectedProduct]);

  return (
    <>
      {attributes
        .map((attributes, key) => (
          <AttributeGroup
            key={key}
            title={key}
            attributes={attributes}
            isCard={false}
            onSelect={({ name, id }) => {
              setSelectedAttributeValues((currentValues) => ({
                ...currentValues,
                [name]: id
              }));
            }}
          />
        ))
        .toArray()}
    </>
  );
}

function SimpleProduct({ product, setSelectedProduct }: ProductDetailsProps) {
  useEffect(() => {
    setSelectedProduct(product);
  }, [product, setSelectedProduct]);
  return null;
}

function ProductDetailsFactory({ product, ...props }: ProductDetailsProps) {
  switch (product.type) {
    case ProductType.SIMPLE:
      return <SimpleProduct product={product} {...props} />;
    case ProductType.VARIABLE:
      return <VariableProduct product={product} {...props} />;
    default:
      throw new Error(
        `Factory Error: Failed to create product component of type "${product?.type}"`
      );
  }
}

function SingleProduct() {
  const { id } = useParams<{ id: string }>();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const {
    data: product,
    isLoading,
    isError
  } = useQuery(['product', id], () => Product.get(id!), {
    enabled: !!id,
    onSuccess: (data) => {
      setSelectedProduct(data);
    }
  });

  const downloadQRCode = () => {
    const name = selectedProduct?.name || 'qr-code';
    const canvas = qrCodeRef.current?.querySelector('canvas');
    if (canvas) {
      const qrCodeURL = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');

      const a = document.createElement('a');
      a.href = qrCodeURL;
      a.download = `${name}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Convert every gallery and cover image to a Media instance for fullUrl
  const gallery = useMemo(() => {
    if (!product) return [];
    const cover = product.coverImage
      ? new Media(product.coverImage)
      : undefined;
    const galleryList = Array.isArray(product.gallery)
      ? product.gallery.map((img: any) =>
          img instanceof Media ? img : new Media(img)
        )
      : [];
    if (cover && !galleryList.some((img) => img.id === cover.id)) {
      return [cover, ...galleryList];
    }
    return galleryList;
  }, [product]);

  // Group attributes/features for compact display
  const attributesGrouped = useMemo(() => {
    if (!product?.attributes) return [];
    const map: Record<string, string[]> = {};
    product.attributes.forEach((av: any) => {
      if (!map[av.name]) map[av.name] = [];
      map[av.name].push(av.value);
    });
    return Object.entries(map);
  }, [product]);

  const featuresGrouped = useMemo(() => {
    if (!product?.features) return [];
    const map: Record<string, string[]> = {};
    product.features.forEach((fv: any) => {
      if (!map[fv.name]) map[fv.name] = [];
      map[fv.name].push(fv.value);
    });
    return Object.entries(map);
  }, [product]);

  if (isLoading) {
    return <div className="cs-content">Učitava se proizvod...</div>;
  }

  if (isError || !product) {
    return <div className="cs-content">Proizvod nije pronađen.</div>;
  }

  return (
    <div className="cs-content">
      <div className={style.wrapper}>
        <div className={style.gallery}>
          <CarouselProvider
            visibleSlides={1}
            totalSlides={gallery.length}
            naturalSlideWidth={300}
            naturalSlideHeight={400}
            className={style.productCarousel}
            currentSlide={currentSlide}
          >
            <div className={style.thumbs}>
              {gallery.map((item, index) => (
                <Dot
                  key={index}
                  slide={index}
                  className={cx(
                    style.paginationButton,
                    currentSlide === index ? style.selected : ''
                  )}
                  onClick={() => setCurrentSlide(index)}
                >
                  <Image
                    src={item.fullUrl}
                    hasMasterSpinner={false}
                    alt={`${product.name} - slika ${index + 1}`}
                  />
                </Dot>
              ))}
            </div>
            <Slider className={style.mainSlider}>
              {gallery.map((item, index) => (
                <Slide key={index} index={index}>
                  <Image
                    src={item.fullUrl}
                    alt={`${product.name} - slika ${index + 1}`}
                    hasMasterSpinner={false}
                  />
                </Slide>
              ))}
            </Slider>
          </CarouselProvider>
        </div>
        <div className={style.info}>
          <h4 className={style.subtitle}>{selectedProduct?.category?.name}</h4>
          <div className={style.titleContainer}>
            <h1 className={style.title}>{selectedProduct?.name}</h1>
          </div>
          <p className={style.description}>SKU: {selectedProduct?.sku}</p>
          <div className={style.price}>
            {selectedProduct?.price}&nbsp;
            {process.env.REACT_APP_DEFAULT_CURRENCY || 'RSD'}
          </div>
          {/* --- Attributes & Features in Two Cards Below Price --- */}
          <div className={style.extraDetailsRow}>
            {attributesGrouped.length > 0 && (
              <div className={style.attrFeatureBox}>
                <ul className={style.attrFeatureList}>
                  {attributesGrouped.map(([attrName, values]) => (
                    <li key={attrName}>
                      <strong>{attrName}:</strong> {values.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {featuresGrouped.length > 0 && (
              <div className={style.attrFeatureBox}>
                <ul className={style.attrFeatureList}>
                  {featuresGrouped.map(([featureName, values]) => (
                    <li key={featureName}>
                      <strong>{featureName}:</strong> {values.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* QR code modal (hidden) */}
      <button className="cs-button-primary" onClick={downloadQRCode}>
        <MdQrCode /> Preuzmi QR
      </button>
      <div ref={qrCodeRef} style={{ display: 'none' }}>
        <QRCode value={selectedProduct?.sku || ''} size={256} />
      </div>
    </div>
  );
}

export default SingleProduct;
